/* ============================================================
   Operator logo — the one asset here nobody on this side controls.

   The white-label work made the export carry the operator's identity
   instead of ours, and left the last piece open: `buildPrintHeader`
   renders `brand.logo` when a brand has one, falls back to the name
   when it does not, and no brand had one. This is where one comes
   from — the operator's own file, on the operator's own device.

   ---- Why every bound below exists ----

   A logo is the only thing in this product supplied as a binary blob
   by someone outside it, and it lands in two places that punish a bad
   one: a printed page with a fixed header box, and a localStorage
   quota shared with the user's actual saved work.

   1. RE-ENCODED, NEVER STORED AS UPLOADED. The file is drawn to a
      canvas and read back out as PNG. That bounds the byte size at
      something we chose rather than something the operator's design
      agency chose, and — more to the point — a canvas cannot round-trip
      anything that is not an image. A file that is secretly something
      else does not survive the trip.

   2. NO SVG. It is the format a logo most often arrives in, and the
      one this must refuse. An SVG is a document: it carries script,
      external references and entity declarations. Browsers do not
      execute script in an <img>, so this is not an immediate hole —
      but the file also goes into a print header, gets exported in the
      operator's data, and may be handed to a PDF pipeline later, and
      "safe in the one context we happen to use today" is not a
      property worth betting a white-label deployment on. Raster only.

   3. BOUNDED IN PIXELS, NOT ONLY IN BYTES. A 12000×80 banner is a
      small file and still ruins the header. The print box is 14mm tall
      by 60mm wide with object-fit: contain, so anything wildly off that
      ratio renders as a sliver. Downscaled to fit MAX_EDGE, preserving
      aspect, so the stored copy is already the size it will be used at.

   4. BOUNDED IN BYTES AFTER RE-ENCODING TOO. Downscaling usually
      settles this, but a photographic logo at max edge can still come
      out large, and localStorage quota exhaustion here would take the
      user's saved scorecards down with it. Checked at the end, on the
      thing actually being stored, rather than on the upload.

   Every refusal returns a reason meant to be shown to the operator. A
   rejected upload with no explanation reads as a broken feature.
   ============================================================ */

import { session } from './storage.js';

const STORAGE_KEY = 'kanda_operator_logo';

/* Raster formats a canvas can decode everywhere we run. */
export const ACCEPTED_TYPES = Object.freeze(['image/png', 'image/jpeg', 'image/webp']);

/* Rejected before decoding, so a huge file is never read into memory.
   Generous against the stored ceiling because the input is allowed to
   be a print-resolution original; it is the re-encoded copy that has
   to be small. */
const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

/* Longest edge of the stored copy. 600px across the 60mm print box is
   about 254dpi — past what the header needs and short of wasteful. */
const MAX_EDGE = 600;

/* Ceiling on the re-encoded copy. localStorage is typically 5MB for the
   whole origin and the operator's saved work shares it. */
const MAX_STORED_BYTES = 400 * 1024;

/* An image this small is a favicon or a mistake, and scaling it into a
   60mm box produces a blur the operator will blame on us. */
const MIN_EDGE = 32;

/* Short edge of the STORED copy, which bounds aspect ratio at 25:1 once
   MAX_EDGE has been applied. See the check itself for why this cannot
   be folded into MIN_EDGE. */
const MIN_SHORT_EDGE = 24;

/* createImageBitmap decodes off the main thread and is what we want;
   the <img> path is the fallback for browsers without it. Both revoke
   the object URL on the way out — a leaked blob URL pins the whole
   decoded image in memory for the life of the document, which on the
   low-end devices this product targets is the difference between a
   sluggish tab and a killed one. */
async function loadImage(file) {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('decode failed'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function bytesOfDataUrl(dataUrl) {
  const comma = dataUrl.indexOf(',');
  if (comma === -1) return 0;
  /* base64 encodes 3 bytes as 4 chars; padding marks 1–2 unused. */
  const body = dataUrl.length - comma - 1;
  const pad = dataUrl.endsWith('==') ? 2 : dataUrl.endsWith('=') ? 1 : 0;
  return Math.floor((body * 3) / 4) - pad;
}

function readable(bytes) {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} kB`;
}

/**
 * Validate, downscale and re-encode an operator-supplied image.
 *
 * @param {File} file
 * @returns {Promise<{ok: true, dataUrl: string, width: number, height: number, bytes: number}
 *                  | {ok: false, reason: string}>}
 */
export async function prepareLogo(file) {
  if (!file) return { ok: false, reason: 'No file was chosen.' };

  /* Checked before size, because the SVG answer is a different
     conversation from the too-big answer and the operator should get
     the one that applies. */
  if (!ACCEPTED_TYPES.includes(file.type)) {
    const svg = file.type === 'image/svg+xml';
    return {
      ok: false,
      reason: svg
        ? 'SVG logos are not accepted. Export a PNG at around 600px wide — ' +
          'an SVG is a document rather than an image, and this one has to be ' +
          'safe to embed in a report that leaves your device.'
        : `That file is ${file.type || 'of an unknown type'}. Use a PNG, JPEG or WebP.`
    };
  }

  if (file.size > MAX_UPLOAD_BYTES) {
    return {
      ok: false,
      reason: `That file is ${readable(file.size)}. The limit is ${readable(MAX_UPLOAD_BYTES)} — ` +
        'a logo does not need to be a print original.'
    };
  }

  let bitmap;
  try {
    bitmap = await loadImage(file);
  } catch {
    /* Reaching here means the type said image and the decoder disagreed:
       a renamed file, or a truncated one. Either way it is not usable. */
    return { ok: false, reason: 'That file could not be read as an image. It may be damaged.' };
  }

  const { width: w0, height: h0 } = bitmap;
  if (!w0 || !h0) return { ok: false, reason: 'That image reports no dimensions.' };
  if (Math.max(w0, h0) < MIN_EDGE) {
    return {
      ok: false,
      reason: `That image is ${w0}×${h0}px. Anything under ${MIN_EDGE}px on its longest ` +
        'edge will be blurred when scaled into the report header.'
    };
  }

  const scale = Math.min(1, MAX_EDGE / Math.max(w0, h0));
  const width = Math.max(1, Math.round(w0 * scale));
  const height = Math.max(1, Math.round(h0 * scale));

  /* The short edge after scaling, not before.

     MIN_EDGE above guards the longest edge and misses the case this
     catches: a 4000×40 banner is comfortably over MIN_EDGE, is a small
     file, and downscales to 600×6 — six pixels tall in a 14mm box. It
     was accepted by the first version of this and printed as a grey
     smear. The bound belongs after the scale because that is the image
     actually stored and actually rendered.

     MIN_SHORT_EDGE allows a 25:1 wordmark, which is wider than any real
     logo needs, and refuses the banner. */
  if (Math.min(width, height) < MIN_SHORT_EDGE) {
    return {
      ok: false,
      reason: `That image is ${w0}×${h0}px — too long and thin to sit in the report header. ` +
        'Crop it closer to the mark, or use a squarer version.'
    };
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { ok: false, reason: 'This browser could not process the image.' };
  ctx.drawImage(bitmap, 0, 0, width, height);
  if (typeof bitmap.close === 'function') bitmap.close();

  let dataUrl;
  try {
    /* PNG, not JPEG: logos have flat colour and hard edges, which JPEG
       rings around, and most arrive with a transparent background that
       JPEG would fill with black. */
    dataUrl = canvas.toDataURL('image/png');
  } catch {
    /* A canvas tainted by a cross-origin source throws here. Cannot
       happen from a File, but the failure is silent data loss if it
       ever did, so it is caught rather than assumed away. */
    return { ok: false, reason: 'This browser refused to export the processed image.' };
  }

  const bytes = bytesOfDataUrl(dataUrl);
  if (bytes > MAX_STORED_BYTES) {
    return {
      ok: false,
      reason: `Processed to ${readable(bytes)}, over the ${readable(MAX_STORED_BYTES)} limit. ` +
        'A logo with fewer colours or less photographic detail will compress much further.'
    };
  }

  return { ok: true, dataUrl, width, height, bytes };
}

/** @returns {boolean} false if storage refused it — already announced. */
export function saveLogo(prepared) {
  if (!prepared?.ok) return false;
  return session.set(STORAGE_KEY, {
    dataUrl: prepared.dataUrl,
    width: prepared.width,
    height: prepared.height,
    bytes: prepared.bytes,
    savedAt: new Date().toISOString()
  });
}

/**
 * The stored logo, or null.
 *
 * Re-validated on read rather than trusted. localStorage is editable by
 * anyone with the console open, and this value is interpolated into an
 * <img src>. A stored string beginning `javascript:` or pointing at a
 * remote host must not reach the DOM just because it was in storage
 * once — so the shape is checked every time, not only on the way in.
 */
export function getLogo() {
  const stored = session.get(STORAGE_KEY);
  if (!stored || typeof stored.dataUrl !== 'string') return null;
  if (!/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/.test(stored.dataUrl)) return null;
  return stored;
}

export function clearLogo() {
  session.remove(STORAGE_KEY);
}
